import React from 'react';
import { getThemeColors, spacing, typography, borderRadius } from '../utils/theme';

interface ChamaDashboardTabBarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const ChamaDashboardTabBar: React.FC<ChamaDashboardTabBarProps> = ({ activeTab, onTabChange }) => {
  // Use window.location for navigation since we're outside router context
  const [currentPath, setCurrentPath] = React.useState(window.location.pathname);
  const currentPathRef = React.useRef(currentPath);

  // Listen for navigation changes
  React.useEffect(() => {
    const handleNavigation = () => {
      const newPath = window.location.pathname;
      setCurrentPath(newPath);
      currentPathRef.current = newPath;
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleNavigation);

    // Listen for any location changes by polling
    const interval = setInterval(() => {
      const newPath = window.location.pathname;
      if (newPath !== currentPathRef.current) {
        setCurrentPath(newPath);
        currentPathRef.current = newPath;
      }
    }, 100);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      clearInterval(interval);
    };
  }, []);

  const isChamaDashboardPage = (pathname: string) => {
    const normalizedPath = pathname.replace(/\/$/, ''); // Remove trailing slash
    return normalizedPath.startsWith('/dashboard/chama/');
  };

  // Only show tab bar on chama dashboard pages
  if (!isChamaDashboardPage(currentPath)) {
    return null;
  }

  const colors = getThemeColors('dark');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '🏠' },
    { id: 'members', label: 'Members', icon: '👥' },
    { id: 'contributions', label: 'Contributions', icon: '💰' },
    { id: 'transactions', label: 'Transactions', icon: '📄' },
    { id: 'loans', label: 'Loans', icon: '💳' },
    { id: 'meetings', label: 'Meetings', icon: '📅' },
  ];

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: colors.surface,
      borderTop: `1px solid ${colors.border}`,
      padding: `${spacing.sm} ${spacing.md}`,
      display: 'flex',
      flexDirection: 'row',
      height: '70px',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
    },
    tabItem: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: spacing.xs,
      paddingBottom: spacing.xs,
    },
    tabButton: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '44px',
      paddingLeft: spacing.xs,
      paddingRight: spacing.xs,
      cursor: 'pointer',
      borderRadius: borderRadius.sm,
      transition: 'all 0.2s ease',
      padding: spacing.sm,
    },
    tabIcon: {
      fontSize: '20px',
      marginBottom: spacing.xs,
    },
    tabLabel: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
      textAlign: 'center',
    },
  };

  return (
    <div style={styles.container}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;

        return (
          <div
            key={tab.id}
            style={styles.tabItem}
            onClick={() => onTabChange(tab.id)}
          >
            <div
              style={{
                ...styles.tabButton,
                backgroundColor: isActive ? colors.primary + '15' : 'transparent',
              }}
            >
              <div style={styles.tabIcon}>{tab.icon}</div>
              <div
                style={{
                  ...styles.tabLabel,
                  color: isActive ? colors.primary : colors.textSecondary,
                }}
              >
                {tab.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ChamaDashboardTabBar;